import { Body1, FlowHeader, H2, styled } from "@horizontal-org/shira-ui";
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { AssignLearnersTable } from "../AssignLearnersTable";
import { AssignRequest, getUnassignedLearners } from "../../../../fetch/learner_quiz";
import { AssignLearnerAction } from "../AssignLearnerAction";
import { fetchLearners } from "../../../../fetch/learner";

interface Props {
  quizId: number;
  title: string;
  onExit: () => void;
  openErrorModal: (content: string, retry: () => void) => void;
  onSuccess: () => void;
}

export const AssignLearnersLayover: FunctionComponent<Props> = ({
  quizId,
  title,
  onExit,
  openErrorModal,
  onSuccess
}) => {

  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [hasRegisteredLearners, setHasRegisteredLearners] = useState(false);

  const fetchLearnerQuiz = useCallback(async () => {
    try {
      setLoading(true);
      const [unassignedLearners, learners] = await Promise.all([
        getUnassignedLearners(quizId),
        fetchLearners(),
      ]);
      setData(unassignedLearners);
      setHasRegisteredLearners(learners.some((learner: { status?: string }) => learner.status === "registered"));
    } catch (e) {
      console.log("🚀 ~ fetchLeaners ~ e:", e);
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  const selectedLearners = useMemo<AssignRequest[]>(
    () =>
      Object.entries(rowSelection)
        .filter(([, isSelected]) => Boolean(isSelected))
        .map(([id]) => ({
          learnerId: Number(id),
          quizId,
        })),
    [rowSelection, quizId]
  );

  useEffect(() => {
    fetchLearnerQuiz();
  }, [fetchLearnerQuiz]);

  return (
    <Wrapper>
      <FlowHeader
        onExit={onExit}
        title={title}
        actions={(
          <AssignLearnerAction
            learners={selectedLearners}
            openErrorModal={openErrorModal}
            loading={loading}
            setIsLoading={setLoading}
            disabled={rowSelection && Object.keys(rowSelection).length === 0}
            onSuccess={() => {
              setRowSelection({});
              fetchLearnerQuiz();
              onSuccess();
            }}
          />
        )}
      />
      <Content>
        <div>
          <Header>
            <H2 id="registered-learners-title">{t('learners.assign_dialog.registered_title')}</H2>
            <Body1 id="registered-learners-subtitle">
              {t('learners.assign_dialog.registered_description')}
            </Body1>
            <Body1>
              <Trans
                i18nKey="learners.assign_dialog.registered_description_2"
                components={[<StyledLink to="/learner" />, <StyledLink to="https://shira.app/help/" />]}
              />
            </Body1>
          </Header>

          <AssignLearnersTable
            data={data}
            loading={loading}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            hasRegisteredLearners={hasRegisteredLearners}
          />
        </div>
      </Content>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  inset-inline-start: 0;
  overflow: hidden;
  width: 100vw;
  height: 100vh;
  z-index: 2;
  background: ${props => props.theme.colors.light.paleGrey};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 30px 0;
  max-width: 1200px;
  margin: 0 auto;
  > p {
    padding-top: 12px; 
  }
`;

const Content = styled.div`
  width: 100%;
  box-sizing: border-box;
  overflow-y: scroll;
  padding: 0 32px 48px;

  > div {
    width: 100%;
    max-width: ${props => props.theme.breakpoints.md};
    margin: 0 auto;
  }
`;

const StyledLink = styled(Link)`
  font-weight: 700;
  color: ${props => props.theme.primary.base};
  text-decoration-color: ${props => props.theme.primary.base};
`;
